/** Landsnummer och hjälpare för telefonfält med obligatorisk landskod. */
import { COUNTRIES, type Country } from "@/lib/countries";

/** ISO-kod -> landsnummer (utan +). */
export const DIAL_CODES: Record<string, string> = {
  AF: "93", AL: "355", DZ: "213", AR: "54", AU: "61", AT: "43", BE: "32",
  BA: "387", BR: "55", BG: "359", CA: "1", CL: "56", CN: "86", CO: "57",
  CR: "506", HR: "385", CY: "357", CZ: "420", DK: "45", EG: "20", EE: "372",
  FI: "358", FR: "33", DE: "49", GR: "30", HU: "36", IS: "354", IN: "91",
  ID: "62", IR: "98", IQ: "964", IE: "353", IL: "972", IT: "39", JP: "81",
  KR: "82", LV: "371", LB: "961", LT: "370", LU: "352", MX: "52", MD: "373",
  MA: "212", NL: "31", NZ: "64", NO: "47", PK: "92", PL: "48", PT: "351",
  RO: "40", RU: "7", RS: "381", SK: "421", SI: "386", ZA: "27", ES: "34",
  SE: "46", CH: "41", SY: "963", TR: "90", UA: "380", GB: "44", US: "1",
  UY: "598", VE: "58", VN: "84", SD: "249",
};

export const DEFAULT_PHONE_COUNTRY = "ES";
const TOP_PHONE_CODES = ["ES", "SE", "GB", "NO", "DK", "FI", "DE"];

export type PhoneCountry = Country & { dial: string };

export const PHONE_COUNTRIES: PhoneCountry[] = COUNTRIES.filter(
  (c) => DIAL_CODES[c.code],
).map((c) => ({ ...c, dial: DIAL_CODES[c.code] as string }));

/** Vanligast överst, sedan A–Ö. */
export function phoneCountryList(): { country: PhoneCountry; group: string }[] {
  const top = TOP_PHONE_CODES.map((code) =>
    PHONE_COUNTRIES.find((c) => c.code === code),
  ).filter((c): c is PhoneCountry => Boolean(c));
  const rest = [...PHONE_COUNTRIES].sort((a, b) => a.name.localeCompare(b.name, "sv"));
  return [
    ...top.map((country) => ({ country, group: "Vanligast" })),
    ...rest.map((country) => ({ country, group: "Alla länder A–Ö" })),
  ];
}

function digits(value: string): string {
  return value.replace(/\D+/g, "");
}

/** Delar upp ett lagrat nummer i landskod + lokal del. */
export function splitPhone(
  value: string,
  fallbackCountry = DEFAULT_PHONE_COUNTRY,
): { country: string; local: string } {
  const raw = (value ?? "").trim();
  if (raw.startsWith("+")) {
    const rest = digits(raw);
    // Längsta landsnummer först så +46 inte matchar före +469.
    const sorted = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    const hit = sorted.find((c) => rest.startsWith(c.dial));
    if (hit) {
      return { country: hit.code, local: rest.slice(hit.dial.length) };
    }
    return { country: fallbackCountry, local: rest };
  }
  return { country: fallbackCountry, local: digits(raw) };
}

/** Sätter ihop landskod och lokalt nummer till E.164-liknande format. */
export function joinPhone(countryCode: string, local: string): string {
  const dial = DIAL_CODES[countryCode] ?? DIAL_CODES[DEFAULT_PHONE_COUNTRY];
  const rest = digits(local).replace(new RegExp(`^0+`), "");
  if (!rest) return "";
  return `+${dial}${rest}`;
}

/** Visningsformat: +34 612 34 56 78 */
export function formatPhone(value: string): string {
  const { country, local } = splitPhone(value);
  if (!local) return "";
  const dial = DIAL_CODES[country] ?? "";
  const groups = local.match(/.{1,3}/g) ?? [local];
  return `+${dial} ${groups.join(" ")}`;
}

/**
 * Känner igen ett landsnummer i början av en siffersträng – antingen efter
 * "00" (0046…) eller direkt (46… från inklistrat +46…). Returnerar landet och
 * det återstående lokala numret, eller null om inget känt prefix matchar
 * eller resten har orimlig längd.
 */
export function detectDialPrefix(
  digitsOnly: string,
): { country: string; local: string } | null {
  const d = digits(digitsOnly);
  if (!d) return null;
  const intl = d.startsWith("00") ? d.slice(2) : d;
  const sorted = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  const hit = sorted.find((c) => intl.startsWith(c.dial));
  if (!hit) return null;
  const local = intl.slice(hit.dial.length).replace(/^0+/, "");
  if (local.length < 6 || local.length > 14) return null;
  return { country: hit.code, local };
}

/** Giltigt = landskod finns och 6–14 siffror i den lokala delen. */
export function isValidPhone(value: string): boolean {
  const raw = (value ?? "").trim();
  if (!raw.startsWith("+")) return false;
  const { country, local } = splitPhone(raw);
  if (!DIAL_CODES[country]) return false;
  return local.length >= 6 && local.length <= 14;
}
